//// Manage sessions across *.dhsb.org subdomains
import initSentry from './sentry.js';
const Sentry = initSentry();

import { auth } from './firebase.js';
import { API_HOST } from '../index.js';
import UAParser from 'ua-parser-js';
import { showError } from './error.js';
import { hideLoadingOverlay, showLoadingOverlay } from './animate.js';
import { returnToAuth } from './auth-ui.js';
import { showConfirm } from './confirm.js';
import { resetSettingsState } from './settings-state.js';

export async function fetchFirebaseToken() {
    const response = await fetch(`${API_HOST}/auth/get-token`, {
        method: 'GET',
        credentials: 'include'
    });
    if (response.ok) {
        const data = await response.json();
        return data;
    } else if (response.status === 401) {
        console.warn('Session expired, need to re-auth');
        throw new Error('Session expired');
    } else if (response.status === 404) {
        console.warn('No session found');
        throw new Error('No session found');
    } else if (response.status === 500) {
        console.error('Server error:', response.statusText);
        throw new Error('Server error');
    }
}

export async function postIdTokenToSessionLogin(idToken, samlProfile = null) {
    const parser = new UAParser();
    const uaResult = parser.getResult();

    const deviceInfo = {
        deviceType: uaResult.device.type || 'Unknown',
        os: uaResult.os.name || 'Unknown',
        osVersion: uaResult.os.version || 'Unknown',
        browser: uaResult.browser.name || 'Unknown',
        browserVersion: uaResult.browser.version || 'Unknown'
    };

    const response = await fetch(`${API_HOST}/auth/login`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ idToken, deviceInfo, samlProfile })
    });

    if (!response.ok) {
        throw new Error('Network response was not ok');
    }

    return await response.json();
}

export async function logout(message = 'You have been successfully logged out.', showLoginOptions = true) {
    const response = await fetch(`${API_HOST}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
    });

    if (!response.ok) {
        await hideLoadingOverlay();
        throw new Error('Network response was not ok');
    }

    const data = await response.json();
    if (response.ok && data.status === 'success') {
        await auth.signOut();
        Sentry.setUser(null);
        window.user = null;
        resetSettingsState();
        window.meProfile = null;

        await hideLoadingOverlay();
        await returnToAuth({ message, showLogin: showLoginOptions });
    } else {
        await hideLoadingOverlay();
        throw new Error('Network response was not ok');
    }
}

function formatSessionDate(lastActive) {
    const timestamp = lastActive._seconds * 1000 + Math.floor(lastActive._nanoseconds / 1000000);
    return new Date(timestamp).toLocaleString();
}

function createSessionBadge(isCurrent) {
    if (!isCurrent) {
        return '';
    }
    return '<span class="session-badge">This device</span>';
}

async function handleTerminateSession(sessionId) {
    const confirmed = await showConfirm(
        'Terminate session?',
        'This will sign out that device from your DHSB account.',
        'Terminate'
    );

    if (!confirmed) {
        return;
    }

    const elements = document.querySelectorAll(`[data-session-id="${sessionId}"]`);
    elements.forEach((el) => {
        el.querySelector('.terminate-session-btn')?.setAttribute('aria-busy', 'true');
    });

    try {
        await terminateSession(sessionId);
        elements.forEach((el) => el.remove());
    } catch (error) {
        console.error(error);
        showError('There was an error terminating the session.', 'Please try again later.');
        elements.forEach((el) => {
            el.querySelector('.terminate-session-btn')?.setAttribute('aria-busy', 'false');
        });
    }
}

function buildSessionRow(session) {
    const row = document.createElement('tr');
    row.dataset.sessionId = session.__id;
    if (session.thisSession) {
        row.classList.add('this-session');
    }

    row.innerHTML = `
        <td>${session.os} ${session.osVersion}${session.thisSession ? ' <span class="session-badge">This device</span>' : ''}</td>
        <td>${session.browser} ${session.browserVersion}</td>
        <td>${formatSessionDate(session.lastActive)}</td>
        <td>${session.ip || 'Unknown'}</td>
        <td></td>`;

    const actionCell = row.lastElementChild;
    if (!session.thisSession) {
        const terminateBtn = document.createElement('button');
        terminateBtn.type = 'button';
        terminateBtn.classList.add('btn', 'btn-danger', 'terminate-session-btn');
        terminateBtn.textContent = 'Terminate';
        terminateBtn.addEventListener('click', () => handleTerminateSession(session.__id));
        actionCell.appendChild(terminateBtn);
    }

    return row;
}

function buildSessionCard(session) {
    const card = document.createElement('article');
    card.classList.add('session-card');
    card.setAttribute('role', 'listitem');
    card.dataset.sessionId = session.__id;

    if (session.thisSession) {
        card.classList.add('this-session');
    }

    card.innerHTML = `
        <div class="session-card-header">
          <strong>${session.browser} ${session.browserVersion}</strong>
          ${createSessionBadge(session.thisSession)}
        </div>
        <p><span class="session-card-label">System</span> ${session.os} ${session.osVersion}</p>
        <p><span class="session-card-label">Last active</span> ${formatSessionDate(session.lastActive)}</p>
        <p><span class="session-card-label">IP address</span> ${session.ip || 'Unknown'}</p>`;

    if (!session.thisSession) {
        const terminateBtn = document.createElement('button');
        terminateBtn.type = 'button';
        terminateBtn.classList.add('btn', 'btn-danger', 'terminate-session-btn');
        terminateBtn.textContent = 'Terminate session';
        terminateBtn.addEventListener('click', () => handleTerminateSession(session.__id));
        card.appendChild(terminateBtn);
    }

    return card;
}

export function setSessionsLoading(isLoading) {
    const loading = document.getElementById('active-sessions-loading');
    const table = document.getElementById('active-sessions-list');
    const cards = document.getElementById('active-sessions-cards');

    loading.classList.toggle('is-hidden', !isLoading);
    table.classList.toggle('is-hidden', isLoading);
    cards.classList.toggle('is-hidden', isLoading);
}

export async function loadActiveSessions() {
    const sessionsList = document.getElementById('active-sessions-list').querySelector('tbody');
    const sessionsCards = document.getElementById('active-sessions-cards');
    sessionsList.innerHTML = '';
    sessionsCards.innerHTML = '';
    setSessionsLoading(true);

    try {
        const sessionResponse = await fetchUserSessions();
        const userSessions = sessionResponse.data || [];
        const section = document.querySelector('.settings-sessions');

        if (userSessions.length === 0) {
            section.classList.add('is-hidden');
            return;
        }

        section.classList.remove('is-hidden');

        userSessions.forEach((session) => {
            sessionsList.appendChild(buildSessionRow(session));
            sessionsCards.appendChild(buildSessionCard(session));
        });
    } catch (error) {
        console.error('Error loading active sessions:', error);
        showError('Failed to load active sessions.', 'Please try again later.');
    } finally {
        setSessionsLoading(false);
    }
}

async function fetchUserSessions() {
    const response = await fetch(`${API_HOST}/auth/sessions`, {
        method: 'GET',
        credentials: 'include'
    });
    if (!response.ok) {
        throw new Error('Failed to fetch sessions');
    }
    return await response.json();
}

async function terminateSession(sessionId) {
    const response = await fetch(`${API_HOST}/auth/terminate-session`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ targetSession: sessionId }),
    });
    if (!response.ok) {
        throw new Error('Failed to terminate session');
    }
    return response.json().then(data => data.status === 'success');
}

export async function checkSessionValidity() {
    if (!window.loginComplete) {
        return null;
    }

    const response = await fetch(`${API_HOST}/auth/check-session`, {
        method: 'GET',
        credentials: 'include',
    });

    if (!response.ok) {
        console.error('Session is no longer valid');
        showLoadingOverlay('Session expired…');
        await logout('Your session has expired. Please log in again.', true);
        return false;
    }

    return true;
}
